// ============================================
// FORMATADORES
// ============================================

/**
 * Formata valor para moeda brasileira (BRL)
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Formata data para formato brasileiro
 */
export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('pt-BR').format(new Date(date));
}

/**
 * Formata data e hora para formato brasileiro
 */
export function formatDateTime(date: string | Date): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(date));
}

/**
 * Formata CPF (000.000.000-00)
 */
export function formatCPF(cpf: string): string {
  const cleaned = cpf.replace(/\D/g, '');
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Formata CNPJ (00.000.000/0000-00)
 */
export function formatCNPJ(cnpj: string): string {
  const cleaned = cnpj.replace(/\D/g, '');
  return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

/**
 * Formata CPF ou CNPJ automaticamente
 */
export function formatCPFCNPJ(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 11) {
    return formatCPF(cleaned);
  }
  return formatCNPJ(cleaned);
}

/**
 * Formata telefone brasileiro
 */
export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return phone;
}

/**
 * Formata CEP (00000-000)
 */
export function formatCEP(cep: string): string {
  const cleaned = cep.replace(/\D/g, '');
  return cleaned.replace(/(\d{5})(\d{3})/, '$1-$2');
}

/**
 * Formata quantidade com unidade de medida
 */
export function formatQuantidade(quantidade: number, unidade: string): string {
  const unidadeLabel: Record<string, string> = {
    UN: 'un',
    M: 'm',
    M2: 'm²',
    M3: 'm³',
    KG: 'kg',
  };

  const decimals = ['M', 'M2', 'M3', 'KG'].includes(unidade) ? 3 : 0;

  return `${quantidade.toFixed(decimals)} ${unidadeLabel[unidade] || unidade}`;
}

/**
 * Formata numero com casas decimais
 */
export function formatNumber(value: number, decimals: number = 2): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Formata status da venda
 */
export function formatStatusVenda(status: string): string {
  const statusLabel: Record<string, string> = {
    PENDENTE: 'Pendente',
    FINALIZADA: 'Finalizada',
    CANCELADA: 'Cancelada',
  };
  return statusLabel[status] || status;
}

/**
 * Formata forma de pagamento
 */
export function formatFormaPagamento(forma: string): string {
  const formaLabel: Record<string, string> = {
    DINHEIRO: 'Dinheiro',
    CARTAO_CREDITO: 'Cartão de Crédito',
    CARTAO_DEBITO: 'Cartão de Débito',
    PIX: 'PIX',
    FIADO: 'Fiado',
    CHEQUE: 'Cheque',
  };
  return formaLabel[forma] || forma;
}

/**
 * Formata perfil de usuario
 */
export function formatPerfil(perfil: string): string {
  const perfilLabel: Record<string, string> = {
    ADMIN: 'Administrador',
    GERENTE: 'Gerente',
    CAIXA: 'Caixa',
  };
  return perfilLabel[perfil] || perfil;
}

/**
 * Formata unidade de medida
 */
export function formatUnidadeMedida(unidade: string): string {
  const unidadeLabel: Record<string, string> = {
    UN: 'Unidade',
    M: 'Metro',
    M2: 'Metro²',
    M3: 'Metro³',
    KG: 'Quilograma',
  };
  return unidadeLabel[unidade] || unidade;
}
